import * as React from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { ArrowForward } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router';
import { useNotification } from './CustomNotification';
import {
  getOne as getOffer,
  updateOne as updateOffer,
  uploadGalleryImages,
  validate as validateOffer,
  type Offer,
} from '../data/offers';
import { fetchImageAsFile } from '../services/api';
import OfferForm, {
  type FormFieldValue,
  type OfferFormState,
} from './OfferForm';
import PageContainer from './PageContainer';
import {useLanguage} from "../../contexts/LanguageContext";

function OfferEditForm({
  initialValues,
  onSubmit,
}: {
  initialValues: Partial<OfferFormState['values']>;
  onSubmit: (formValues: Partial<OfferFormState['values']>) => Promise<void>;
}) {
  const { offerId } = useParams();
  const navigate = useNavigate();
  const notifications = useNotification();
  const { t } = useLanguage();

  const [formState, setFormState] = React.useState<OfferFormState>(() => ({
    values: initialValues,
    errors: {},
  }));
  const formValues = formState.values;
  const formErrors = formState.errors;

  // Store initial values for comparison
  const initialValuesRef = React.useRef(initialValues);

  const setFormValues = React.useCallback(
    (newFormValues: Partial<OfferFormState['values']>) => {
      setFormState((previousState) => ({
        ...previousState,
        values: newFormValues,
      }));
    },
    [],
  );

  const setFormErrors = React.useCallback(
    (newFormErrors: Partial<OfferFormState['errors']>) => {
      setFormState((previousState) => ({
        ...previousState,
        errors: newFormErrors,
      }));
    },
    [],
  );

  const handleFormFieldChange = React.useCallback(
    (name: keyof OfferFormState['values'], value: FormFieldValue) => {
      const validateField = async (values: Partial<OfferFormState['values']>) => {
        const { issues } = validateOffer(values);
        const errorMessage = issues?.find((issue) => issue.path?.[0] === name)?.message;
        setFormErrors({
          ...formErrors,
          [name]: errorMessage ? t(errorMessage) : undefined,
        });
      };

      const newFormValues = { ...formValues, [name]: value };

      setFormValues(newFormValues);
      validateField(newFormValues);
    },
    [formValues, formErrors, setFormErrors, setFormValues, t],
  );

  const handleFormReset = React.useCallback(() => {
    setFormValues(initialValues);
  }, [initialValues, setFormValues]);

  const handleFormSubmit = React.useCallback(async () => {
    // Validate form data
    const { issues } = validateOffer(formValues);
    const errors: Partial<OfferFormState['errors']> = {};

    if (issues && issues.length > 0) {
      issues.forEach((issue) => {
        if (issue.path?.[0]) {
          errors[issue.path[0] as keyof OfferFormState['errors']] = t(issue.message);
        }
      });
    }

    // Validate images (only if new images are being uploaded)
    const images = formValues.images as File[] | undefined;
    if (images && images.length === 0) {
      errors.images = t('validation.atLeastOneImageRequired');
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    setFormErrors({});

    // Check if there are any changes
    const hasTextChanges =
      formValues.title !== initialValuesRef.current.title ||
      formValues.description !== initialValuesRef.current.description ||
      formValues.cost_description !== initialValuesRef.current.cost_description ||
      formValues.seats_total !== initialValuesRef.current.seats_total ||
      formValues.scope !== initialValuesRef.current.scope ||
      formValues.country_id !== initialValuesRef.current.country_id ||
      formValues.provider_name !== initialValuesRef.current.provider_name ||
      formValues.provider_phone_number !== initialValuesRef.current.provider_phone_number ||
      formValues.fly_tickets_cost !== initialValuesRef.current.fly_tickets_cost ||
      formValues.accommodation_cost !== initialValuesRef.current.accommodation_cost ||
      formValues.tours_cost !== initialValuesRef.current.tours_cost ||
      formValues.others_cost !== initialValuesRef.current.others_cost;

    const hasNewImages = images && images.length > 0;

    const initialGalleryUrls = initialValuesRef.current.gallery_image_urls || [];
    const currentGalleryUrls = formValues.gallery_image_urls || [];
    const hasGalleryChanges =
      initialGalleryUrls.length !== currentGalleryUrls.length ||
      initialGalleryUrls.some((url, index) => url !== currentGalleryUrls[index]);

    if (!hasTextChanges && !hasNewImages && !hasGalleryChanges) {
      notifications.show(t('offers.noChanges'), {
        severity: 'info',
        autoHideDuration: 3000,
      });
      return;
    }

    try {
      await onSubmit(formValues);
      notifications.show(t('offers.updateSuccess'), {
        severity: 'success',
        autoHideDuration: 3000,
      });

      navigate(`/offers/${offerId}`);
    } catch (editError) {
      notifications.show(
        `Failed to edit offer. Reason: ${(editError as Error).message}`,
        {
          severity: 'error',
          autoHideDuration: 3000,
        },
      );
      throw editError;
    }
  }, [formValues, navigate, notifications, onSubmit, setFormErrors, t]);

  return (
    <OfferForm
      formState={formState}
      onFieldChange={handleFormFieldChange}
      onSubmit={handleFormSubmit}
      onReset={handleFormReset}
      submitButtonLabel={t('common.save')}
      backButtonPath={`/offers/${offerId}`}
    />
  );
}

export default function OfferEdit() {
  const { offerId } = useParams();

  const [offer, setOffer] = React.useState<Offer | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<Error | null>(null);

  const loadData = React.useCallback(async () => {
    setError(null);
    setIsLoading(true);

    try {
      const showData = await getOffer(Number(offerId));

      setOffer(showData);
    } catch (showDataError) {
      setError(showDataError as Error);
    }
    setIsLoading(false);
  }, [offerId]);

  React.useEffect(() => {
    loadData();
  }, [loadData]);

  const { t } = useLanguage();
  const handleSubmit = React.useCallback(
    async (formValues: Partial<OfferFormState['values']>) => {
      // Step 1: Convert existing image URLs to Files
      const existingImageFiles: File[] = [];
      const existingUrls = formValues.gallery_image_urls || [];

      for (const url of existingUrls) {
        try {
          const file = await fetchImageAsFile(url);
          existingImageFiles.push(file);
        } catch (error) {
          console.error('Failed to fetch existing image:', url, error);
          throw new Error(`Failed to download existing image: ${url}`);
        }
      }

      // Step 2: Combine existing and new images
      const newImages = (formValues.images as File[]) || [];
      const allImages = [...existingImageFiles, ...newImages];

      // Step 4: Update offer with all fields
      const offerData = {
        title: formValues.title,
        description: formValues.description,
        cost_description: formValues.cost_description,
        seats_total: formValues.seats_total,
        country_id: formValues.country_id,
        provider_name: formValues.provider_name,
        provider_phone_number: formValues.provider_phone_number,
        fly_tickets_cost: formValues.fly_tickets_cost,
        accommodation_cost: formValues.accommodation_cost,
        tours_cost: formValues.tours_cost,
        others_cost: formValues.others_cost,
      };

      const updatedData = await updateOffer(Number(offerId), offerData);

      setOffer(updatedData);
      // Step 3: Upload all images together
      if (allImages.length > 0) {
        await uploadGalleryImages(Number(offerId), allImages);
      }
    },
    [offerId],
  );

  const renderEdit = React.useMemo(() => {
    if (isLoading) {
      return (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            m: 1,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }
    if (error) {
      return (
        <Box sx={{ flexGrow: 1 }}>
          <Alert severity="error">{error.message}</Alert>
        </Box>
      );
    }

    return offer ? (
      <OfferEditForm initialValues={offer} onSubmit={handleSubmit} />
    ) : null;
  }, [isLoading, error, offer, handleSubmit]);

  const { direction } = useLanguage();
  const isRtl = direction === 'rtl';
  const navigate = useNavigate();

  const handleBack = React.useCallback(() => {
    navigate(`/offers/${offerId}`);
  }, [navigate, offerId]);

  return (
    <PageContainer
      breadcrumbs={[
        { title: t('offers.title'), path: '/offers' },
        { title: t('offers.view'), path: `/offers/${offerId}` },
        { title: t('offers.edit') },
      ]}
      backButton={
        <IconButton
          onClick={handleBack}
          sx={{
            backgroundColor: 'transparent',
            borderRadius: '50%',
            '&:hover': {
              backgroundColor: '#31394a',
              color: 'white'
            },
          }}
          aria-label="back"
        >
          {isRtl ? <ArrowForward /> : <ArrowBackIcon />}
        </IconButton>
      }
    >
      <Box sx={{ display: 'flex', flex: 1 }}>{renderEdit}</Box>
    </PageContainer>
  );
}

